import { useEffect, useState, useMemo } from "react";
import "./style.css";

export default function OutgoingCallModal({ call, recipient, onEndCall }) {
  const [callDuration, setCallDuration] = useState(0);

  // Memoize the call ID to prevent unnecessary re-renders
  const callId = useMemo(() => call?._id, [call?._id]);
  const recipientId = useMemo(() => recipient?._id, [recipient?._id]);

  useEffect(() => {
    console.log("📞 OutgoingCallModal mounted:", { callId, recipientId });
    
    // Start call duration timer
    const timer = setInterval(() => {
      setCallDuration(prev => prev + 1);
    }, 1000);

    return () => {
      console.log("📞 OutgoingCallModal unmounting");
      clearInterval(timer);
    };
  }, [callId, recipientId]); // Only depend on IDs, not the full objects

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const callTypeText = useMemo(() => call.type === 'video' ? 'Video Call' : 'Voice Call', [call.type]);
  const callIcon = useMemo(() => call.type === 'video' ? '📹' : '📞', [call.type]);

  // Only log on mount or when call ID changes, not every render
  useEffect(() => {
    console.log("📞 OutgoingCallModal render:", { 
      callId: call._id, 
      recipientName: `${recipient.first_name} ${recipient.last_name}`,
      callType: call.type,
      duration: callDuration
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [call._id, recipient._id, call.type]); // Only log when these change, not every second

  return (
    <div className="outgoing_call_overlay">
      <div className="outgoing_call_modal">
        {/* Recipient Info */}
        <div className="recipient_info">
          <div className="recipient_avatar_container">
            <img
              src={recipient.picture}
              alt={recipient.first_name}
              className="recipient_avatar"
              onError={(e) => {
                console.log("📞 Avatar image failed to load, using default");
                e.target.src = '/images/default_profile.png';
              }}
            />
            <div className="call_type_indicator">
              <span className="call_icon">{callIcon}</span>
            </div>
          </div>
          
          <div className="recipient_details">
            <h3 className="recipient_name">
              {recipient.first_name} {recipient.last_name}
            </h3>
            <p className="call_type">{callTypeText}</p>
            <p className="call_duration">{formatDuration(callDuration)}</p>
            <p className="call_status">Calling...</p>
          </div>
        </div>

        {/* Animated Background */}
        <div className="call_animation">
          <div className="pulse_ring"></div>
          <div className="pulse_ring pulse_ring_2"></div>
          <div className="pulse_ring pulse_ring_3"></div>
        </div>

        {/* Call Actions */}
        <div className="call_actions">
          <button
            className="call_action_btn end_call_btn"
            onClick={() => {
              console.log("📞 Call ended by user");
              onEndCall();
            }}
            title="End call"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M6.4 19L5 17.6l5.6-5.6L5 6.4 6.4 5l5.6 5.6L17.6 5 19 6.4 13.4 12l5.6 5.6-1.4 1.4-5.6-5.6z"/>
            </svg>
          </button>
        </div>

        {/* Quick Actions */}
        <div className="quick_actions">
          <button className="quick_action_btn" title="Mute">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z"/>
            </svg>
          </button>
          
          <button className="quick_action_btn" title="Keypad">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </button>
          
          <button className="quick_action_btn" title="Speaker">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z"/>
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
} 